package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.CostFrequency
import com.evoapps.evoshift.domain.model.CostSourceType
import com.evoapps.evoshift.domain.model.FixedCost
import com.evoapps.evoshift.domain.model.InvalidOneOffCostException
import com.evoapps.evoshift.domain.model.LedgerKind
import com.evoapps.evoshift.domain.model.InvalidOneOffCostException.Problem
import com.evoapps.evoshift.domain.model.NotAOneOffCostException
import com.evoapps.evoshift.domain.model.RecurringCostRule
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.todayIn

/*
 * SH-84: one-off expenses belong to a month. A "Feb repair" is a cost document pinned to the
 * month's roster (rosters/{rosterId}/costs, sourceType MANUAL_ONE_OFF, dated inside the month),
 * next to the costs the recurring rules generated. The month's totals (roster_financials, the
 * onRosterCostWritten function) count every cost of the ledger; the daily records count each
 * cost on its day, so the day of the expense is recalculated here. An item can cover several days
 * of the month (date to endDate, e.g. the whole month): the month counts it once, each of its
 * days an equal part (FixedCost.amountOn), and all of its days are recalculated.
 */

/** A one-off expense as typed: name, net amount, a day (or days) of the month, an optional note. */
data class OneOffCostInput(
    val name: String,
    val amount: Double,
    /** The day, or the first day of the period. */
    val date: String,
    val currency: String,
    val note: String? = null,
    /** SH-87: a one-off cost (a repair) or revenue (a catering order). */
    val kind: LedgerKind = LedgerKind.COST,
    /** The last day of the period (inclusive); null or [date] for a single day. */
    val endDate: String? = null
)

/**
 * Recomputes the daily records of [dates] after the month's costs changed, reading the month once
 * and writing the days in one batch (a period can touch every day of the month). A closed day
 * stays as it was.
 */
internal suspend fun recalculateDays(
    calculateDailyRecordUseCase: CalculateDailyRecordUseCase,
    financialRepository: FinancialRepository,
    rosterId: String,
    dates: Collection<String>
) {
    val days = dates.distinct()
    if (days.isEmpty()) return
    val workplaceId = RosterIds.workplaceIdOf(rosterId)
    runCatching {
        val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrThrow().associateBy { it.date }
        val inputs = calculateDailyRecordUseCase.load(workplaceId, rosterId).getOrThrow()
        val records = days.filter { stored[it]?.isLocked != true }.map { date ->
            calculateDailyRecordUseCase.compute(workplaceId, rosterId, date, inputs, DailyRevenueInput.keptFrom(stored[date]))
        }
        if (records.isNotEmpty()) financialRepository.saveDailyRecords(records).getOrThrow()
    }.onFailure { println("Warning: daily records $days of $rosterId not recalculated: ${it.message}") }
}

/**
 * Adds a one-off expense to a month, or changes one ([editingId]). Checks the name, a positive
 * amount and a date inside the roster's month; another kind of cost cannot be changed here.
 */
class AddOneOffCostUseCase(
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase
) {
    suspend operator fun invoke(rosterId: String, input: OneOffCostInput, editingId: String? = null): Result<FixedCost> = runCatching {
        val name = input.name.trim()
        if (name.isEmpty()) throw InvalidOneOffCostException(Problem.NAME_MISSING)
        if (!(input.amount > 0.0) || input.amount.isInfinite()) throw InvalidOneOffCostException(Problem.AMOUNT_NOT_POSITIVE)
        val date = runCatching { LocalDate.parse(input.date) }.getOrNull()
        val (year, month) = RosterIds.yearMonthOf(rosterId)
        if (date == null || date.year != year || date.month.number != month) throw InvalidOneOffCostException(Problem.DATE_OUTSIDE_MONTH)
        val endDate = input.endDate?.takeIf { it.isNotBlank() }?.let { text ->
            val end = runCatching { LocalDate.parse(text) }.getOrNull()
            if (end == null || end.year != year || end.month.number != month) throw InvalidOneOffCostException(Problem.DATE_OUTSIDE_MONTH)
            if (end < date) throw InvalidOneOffCostException(Problem.PERIOD_ENDS_BEFORE_START)
            end.takeIf { it > date }
        }

        val previous = editingId?.let { id ->
            val existing = financialRepository.getFixedCosts(rosterId).getOrThrow().firstOrNull { it.id == id }
                ?: throw NotAOneOffCostException(id)
            if (!existing.isOneOff) throw NotAOneOffCostException(id)
            existing
        }
        val saved = financialRepository.saveFixedCost(
            rosterId,
            FixedCost(
                id = editingId.orEmpty(),
                name = name,
                amount = input.amount,
                currency = input.currency,
                date = date.toString(),
                sourceType = CostSourceType.MANUAL_ONE_OFF,
                note = input.note?.trim()?.takeIf { it.isNotEmpty() },
                originRuleId = previous?.originRuleId,
                kind = input.kind,
                endDate = endDate?.toString()
            )
        ).getOrThrow()
        // The days it is on now, and the days it left when its period changed.
        recalculateDays(calculateDailyRecordUseCase, financialRepository, rosterId, saved.days + previous?.days.orEmpty())
        saved
    }
}

/** Removes a one-off expense from its month (never a rule's cost) and recalculates its days. */
class DeleteOneOffCostUseCase(
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase
) {
    suspend operator fun invoke(rosterId: String, costId: String): Result<Unit> = runCatching {
        val existing = financialRepository.getFixedCosts(rosterId).getOrThrow().firstOrNull { it.id == costId }
            ?: return@runCatching // already gone
        if (!existing.isOneOff) throw NotAOneOffCostException(costId)
        financialRepository.deleteFixedCost(rosterId, costId).getOrThrow()
        recalculateDays(calculateDailyRecordUseCase, financialRepository, rosterId, existing.days)
    }
}

/**
 * Moves the owner's old "one-off" recurring rules (CostFrequency.ONE_OFF, from before SH-84) into
 * months: each becomes a MANUAL_ONE_OFF cost of its workplace's month and the rule is deleted.
 * Rules store no creation date, so the month is the one the migration runs in ([today]) and the
 * cost is dated [today]. A rule whose month has no roster stays as it is (shown as a legacy
 * one-off) until that month exists.
 *
 * Idempotent: the cost's id is derived from the rule (`rule_<ruleId>`), so a migration that
 * stopped between writing the cost and deleting the rule writes the same document again.
 */
class MigrateOneOffCostRulesUseCase(
    private val workplaceRepository: WorkplaceRepository,
    private val rosterRepository: RosterRepository,
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }
) {
    /** The ids of the rules moved into a month. */
    suspend operator fun invoke(ownerId: String, rules: List<RecurringCostRule>): Result<List<String>> = runCatching {
        val day = today()
        rules.filter { it.frequency == CostFrequency.ONE_OFF }.mapNotNull { rule ->
            val rosterId = RosterIds.of(rule.targetWorkplaceId, day.year, day.month.number)
            if (rosterRepository.getRoster(rosterId).isFailure) return@mapNotNull null
            val moved = runCatching {
                financialRepository.saveFixedCost(
                    rosterId,
                    FixedCost(
                        id = costIdOf(rule.id),
                        name = rule.name,
                        amount = rule.amount,
                        currency = rule.currency,
                        date = day.toString(),
                        sourceType = CostSourceType.MANUAL_ONE_OFF,
                        originRuleId = rule.id,
                        kind = rule.kind
                    )
                ).getOrThrow()
                workplaceRepository.deleteRecurringCostRule(ownerId, rule.id).getOrThrow()
                recalculateDays(calculateDailyRecordUseCase, financialRepository, rosterId, listOf(day.toString()))
            }.onFailure { println("Warning: one-off rule ${rule.id} not moved to $rosterId: ${it.message}") }
            rule.id.takeIf { moved.isSuccess }
        }
    }

    companion object {
        /** The migrated cost's document id: the same on every run. */
        fun costIdOf(ruleId: String) = "rule_$ruleId"
    }
}
